import React from 'react';
import { Button } from '../ui/button';
import { cn } from '../ui/utils';
import { CourseThumb } from './CourseThumb';
import { CourseContentType } from '../../types/learning.types';
import { courseContentTypeLabel, durationLabel } from '../../utils/learning';

interface LandingPagePreviewProps {
    title?: string | null;
    description?: string | null;
    categoryName?: string | null;
    categoryColour?: string | null;
    thumbnailUrl?: string | null;
    contentType?: CourseContentType | null;
    durationMinutes?: number | null;
    showInOnboarding?: boolean | null;
    issuesCertificate?: boolean | null;
}

/** Category colours are stored as 6-digit hex (CourseCategory.colour); anything else is treated as unset. */
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;

/**
 * Live preview of the Browse Library card (LEARNING.md §7.4).
 *
 * Renders the same card a learner sees on the library landing page, driven by the form's
 * in-flight values rather than a saved course. Pure presentation: the only interactive
 * element is an inert `Start course` button, and the category colour is the single piece
 * of course data applied inline (DESIGN-LANGUAGE.md §3.3 — everything else is a token).
 */
export const LandingPagePreview: React.FC<LandingPagePreviewProps> = ({
    title,
    description,
    categoryName,
    categoryColour,
    thumbnailUrl,
    contentType,
    durationMinutes,
    showInOnboarding,
    issuesCertificate,
}) => {
    const accent = categoryColour && HEX_COLOUR.test(categoryColour) ? categoryColour : null;

    return (
        <div className="flex flex-col gap-2.5">
            <div className="overflow-hidden rounded-xl border border-border bg-card">
                {/* Header: the uploaded thumbnail, or the category tint + glyph (CourseThumb). */}
                <div className="relative flex h-[120px] items-center justify-center">
                    <CourseThumb
                        thumbnailUrl={thumbnailUrl}
                        categoryColour={categoryColour}
                        iconSize={34}
                    />
                    <span
                        className={cn(
                            'absolute top-2.5 left-2.5 rounded-full border bg-white px-2 py-0.5 text-[11px] font-semibold',
                            !accent && 'border-border text-muted-foreground',
                        )}
                        style={accent ? { color: accent, borderColor: `${accent}4d` } : undefined}
                    >
                        {categoryName || 'No category'}
                    </span>
                    <span className="absolute right-2.5 bottom-2.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        {durationLabel(durationMinutes)}
                    </span>
                </div>

                <div className="flex flex-col gap-2 p-3.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                            {contentType ? courseContentTypeLabel(contentType) : 'PDF document'}
                        </span>
                        {showInOnboarding && (
                            <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-medium text-blue-600">
                                Onboarding
                            </span>
                        )}
                        {issuesCertificate && (
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                                Certificate
                            </span>
                        )}
                    </div>
                    <h3 className="text-[15px] leading-snug font-semibold">
                        {title || 'Course title'}
                    </h3>
                    <p className="text-[13px] leading-relaxed text-muted-foreground">
                        {description || 'Short description shown on the library card.'}
                    </p>
                    <Button type="button" className="mt-1 h-[34px] w-full">
                        Start course
                    </Button>
                </div>
            </div>

            <p className="text-xs leading-relaxed text-muted-foreground">
                This is the exact card learners see in Browse Library. Draft courses are never
                shown until published.
            </p>
        </div>
    );
};

export default LandingPagePreview;
